import React, { memo, useEffect, useRef, useState } from "react";
import LeftPanel from "./LeftPanel";
import styles from "./styles.module.scss";
import RightPanel from "./RightPanel";
import headerStyles from "../AllCoachesPublicSession/styles.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { TimeZoneState } from "app/reducers/scheduling/timeZone";
import { TimeZoneModel } from "app/models/scheduling/TimeZoneModel";
import { useHistory } from "react-router-dom";
import { cls } from "app/utils";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { DateTime } from "luxon";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import SessionsDatePicker from "./_components/SessionsDatePicker";
import { CoachProfileModel } from "app/models";
import earth_icon from "app/images/earth.svg";

const DateTimeCoachSlots = ({ bookSession }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const facilityTimeZone = bookSession?.facility?.timeZone;

  const [date, setDate] = useState(DateTime.fromISO(bookSession?.date));
  const [actualDate, setActualDate] = useState(
    DateTime.fromISO(bookSession?.date),
  );
  const coachesList = bookSession?.facility?.coachProfiles;
  const [coach, setCoach] = useState(bookSession?.coach);
  // Sort rates by the coach's configured order (orderNumber), unordered last
  const sortByOrder = (rates) =>
    [...rates].sort((a, b) => {
      const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

  // Filter out rates with no available payment methods on initial load
  const [lessonRates, setLessonRates] = useState(
    sortByOrder(
      coach?.lessonRates?.filter(rate =>
        rate.availablePaymentMethods?.length > 0
      ) || []
    )
  );
  const [lessonRate, setLessonRate] = useState(bookSession?.pricingValue);
  const [excludeDates, setExcludeDates] = useState<Date[]>([]);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleChangeDate = (value: DateTime) => {
    setDate(value);
    setActualDate(value);
    dispatch(
      setBookSession({
        date: value.toISODate(),
      }),
    );
    setIsCalendarOpen(false);
  };

  const timeZones = useSelector(
    (state: RootState): TimeZoneState => state.schedulingTimeZones,
  );
  const timezone =
    timeZones.data.find(
      (item: TimeZoneModel) => item.value === facilityTimeZone,
    )?.display || "";
  const history = useHistory();

  const fetchExcludedDates = async (currentDate: DateTime) => {
    const startDate = currentDate.minus({ month: 1 });
    const endDate = currentDate.plus({ month: 1 });

    try {
      const params = {
        from: DateTime.local(
          startDate.year,
          startDate.month,
          startDate.daysInMonth - 7 + 1,
        ).toISODate(),
        to: DateTime.local(endDate.year, endDate.month, 7).toISODate(),
        coach_profile_ids: [coach.externalId],
        slot_duration: bookSession.pricingValue.durationInMinutes,
        lesson_rate_id: lessonRate?.id,
      };

      const data = await schedulingFetchGet(
        "/available_dates",
        params,
        {},
        null,
        true,
      );

      const lastMonthDates = Array.from({ length: 7 }, (_, i) =>
        DateTime.local(
          startDate.year,
          startDate.month,
          startDate.daysInMonth - 7 + 1 + i,
        ).toISODate(),
      );

      const currentMonthDates = Array.from(
        { length: currentDate.daysInMonth },
        (_, i) =>
          DateTime.local(
            currentDate.year,
            currentDate.month,
            i + 1,
          ).toISODate(),
      );

      const nextMonthDates = Array.from({ length: 7 }, (_, i) =>
        DateTime.local(endDate.year, endDate.month, i + 1).toISODate(),
      );

      const allDates = [
        ...lastMonthDates,
        ...currentMonthDates,
        ...nextMonthDates,
      ];

      // If API returns empty array, no dates are available - exclude all dates
      if (!data || data.length === 0) {
        const formattedDates = allDates.map((day: string): Date =>
          DateTime.fromISO(day).toJSDate()
        );
        setExcludeDates(formattedDates);
        return;
      }

      if (!data[0].dates) {
        data[0].dates = [];
      }

      const availableDates = new Set(
        data.flatMap((profile: any): void =>
          profile.dates.map((dateString: string): string =>
            DateTime.fromISO(dateString).toISODate(),
          ),
        ),
      );

      const formattedDates = allDates
        .filter((day: string): boolean => !availableDates.has(day))
        .map((day: string): Date => DateTime.fromISO(day).toJSDate());

      setExcludeDates(formattedDates);
    } catch (error) {
      console.error("Error fetching excluded dates:", error);
    }
  };

  useEffect(() => {
    fetchExcludedDates(date);
  }, [date, lessonRate?.id]);

  const handleDecreaseMonth = (decreaseMonth: () => void) => {
    const newDate = actualDate.minus({ months: 1 });
    setActualDate(newDate);
    fetchExcludedDates(newDate);
    decreaseMonth();
  };

  const handleIncreaseMonth = (increaseMonth: () => void) => {
    const newDate = actualDate.plus({ months: 1 });
    setActualDate(newDate);
    fetchExcludedDates(newDate);
    increaseMonth();
  };

  const calendarModalHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCalendarOpen((prev) => !prev);
  };

  const calendarRef = useRef(null);
  const calendarIconRef = useRef(null);

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;

    if (
      calendarRef.current?.contains(target) ||
      calendarIconRef.current?.contains(target)
    ) {
      return;
    }

    setIsCalendarOpen(false);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  const handleCoachChange = (coach: CoachProfileModel) => {
    setCoach(coach);

    // Filter out lesson rates with no available payment methods
    const filteredLessonRates = coach.lessonRates?.filter(rate =>
      rate.availablePaymentMethods?.length > 0
    ) || [];

    // Select default rate from filtered list (not original) to avoid selecting unbookable rate
    const defaultRate = filteredLessonRates.find(lr => lr.defaultRate) || filteredLessonRates[0] || null;

    setLessonRates(sortByOrder(filteredLessonRates));
    setLessonRate(defaultRate);
    dispatch(
      setBookSession({
        coach,
        pricingValue: defaultRate,
        selectedSession: defaultRate,
      }),
    );
  };

  const handleLessonRateChange = (lessonRate) => {
    setLessonRate(lessonRate);
    dispatch(
      setBookSession({
        pricingValue: lessonRate,
        selectedSession: lessonRate,
      }),
    );
  };

  return (
    <div className={headerStyles.container}>
      <div
        className={cls(
          headerStyles.title_container,
          styles.title_container_headerStyles,
        )}
      >
        <div className={styles.title_container}>
          <button
            className={styles.back_button}
            onClick={() => history.goBack()}
          >
            <i className="ico ico-back" />
          </button>
          <h1>{t("Book a Session")}</h1>
        </div>
        <div className={headerStyles.timezone_container}>
          <img src={earth_icon} className={styles.earth_ico} />
          <span>{timezone}</span>
        </div>
        <div className={styles.mobile_calendar_button_container}>
          <div
            className={styles.mobile_calendar_button}
            onClick={calendarModalHandler}
            ref={calendarIconRef}
          >
            <i className={cls("ico ico-calendar", styles.calendar_icon)} />
          </div>

          {isCalendarOpen && (
            <div ref={calendarRef} className={styles.date_picker_mobile}>
              <SessionsDatePicker
                date={date}
                handleDecreaseMonth={handleDecreaseMonth}
                handleIncreaseMonth={handleIncreaseMonth}
                excludeDates={excludeDates}
                handleChangeDate={handleChangeDate}
                className={styles.datepicker_container}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.panel_container}>
        <LeftPanel
          handleDecreaseMonth={handleDecreaseMonth}
          handleIncreaseMonth={handleIncreaseMonth}
          handleChangeDate={handleChangeDate}
          excludeDates={excludeDates}
          date={date}
          handleCoachChange={handleCoachChange}
          lessonRates={lessonRates}
          lessonRate={lessonRate}
          handleLessonRateChange={handleLessonRateChange}
          coach={coach}
          coachesList={coachesList}
        />
        <RightPanel
          bookSession={bookSession}
          lessonRates={lessonRates}
          lessonRate={lessonRate}
          handleLessonRateChange={handleLessonRateChange}
          timezone={timezone}
        />
      </div>
    </div>
  );
};

export default memo(DateTimeCoachSlots);
